import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api, { suppressAxiosLoginRedirectForMs } from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { useUserStore } from './user'
import { decodeJWT } from '@/utils/jwtDecoder'
import type { Credentials } from '@/types/user'

export const useAuthStore = defineStore('auth', () => {
  // Reactive state
  const isAuthenticated = ref(false)
  const user = ref(null)
  const error = ref<string | null>(null)
  const isLoading = ref(false)
  
  // Reactive role state - this ensures role is always in sync with token
  const userRole = ref<string>('')
  const userIsReferrer = ref(false)
  
  // Computed role checks (reactive, unlike localStorage-based checks)
  const isGuide = computed(() => userRole.value === 'GUIDE')
  const isFellow = computed(() => userRole.value === 'FELLOW')
  const isCommon = computed(() => userRole.value === 'COMMON')
  const isMentor = computed(() => userRole.value === 'MENTOR')
  const isReferrer = computed(() => userIsReferrer.value)
  const isAdmin = computed(() => {
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const decoded = decodeJWT(token)
        return decoded?.is_admin === true
      } catch (err) {
        console.warn('[auth.isAdmin] Failed to decode token:', err)
      }
    }
    return false
  })
  
  /**
   * Get user role from cookie (cross-subdomain compatible)
   */
  const getRoleFromCookie = (): string => {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === 'user_role') {
        return (value || '').toUpperCase()
      }
    }
    return ''
  }

  /**
   * Update role from JWT token or cookie
   * Priority: 1) user_role cookie (server's source of truth), 2) JWT token in localStorage
   * 
   * The cookie is prioritized because:
   * - It's set by the server and represents the current role
   * - In cross-subdomain scenarios, localStorage may have stale tokens
   * - When role changes server-side, cookie is updated immediately
   */
  const updateRoleFromToken = () => {
    // First check cookie - it's the server's source of truth
    // This handles cross-subdomain scenarios and role changes correctly
    const roleFromCookie = getRoleFromCookie()
    userRole.value = roleFromCookie || ''
    userIsReferrer.value = false
    
    // Fallback to JWT token in localStorage (for same-domain scenarios without cookie)
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const decoded = decodeJWT(token)
        const roleFromToken = (decoded?.role || '').toUpperCase()
        if (!userRole.value && roleFromToken) {
          userRole.value = roleFromToken
        }
        userIsReferrer.value = decoded?.is_referrer === true
      } catch (err) {
        console.error('Failed to decode token for role:', err)
      }
    }
  }

  const login = async (credentials: Credentials) => {
    const userStore = useUserStore()
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post(API_ROUTES.USER_LOGIN, credentials)
      
      // Cookies are set automatically by backend
      // Store tokens in localStorage as backup
      if (response.data.jwt_access) {
        localStorage.setItem('token', response.data.jwt_access)
      }
      if (response.data.jwt_refresh) {
        localStorage.setItem('refresh_token', response.data.jwt_refresh)
      }

      // Update reactive role from the new token
      updateRoleFromToken()

      // Set authenticated state
      isAuthenticated.value = true
      user.value = response.data.user

      // Fetch user data after login
      await userStore.fetchCurrentUser()
      
      return response.data
    } catch (err: any) {
      if (err.response?.data?.detail) {
        error.value = err.response.data.detail
      } else if (err.response?.status === 401) {
        error.value = 'Invalid credentials. Please try again.'
      } else if (!err.response) {
        error.value = 'Unable to reach the server. Please try again later.'
      } else {
        error.value = 'An unexpected error occurred. Please try again later.'
      }
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    suppressAxiosLoginRedirectForMs()
    try {
      // Call logout endpoint to delete cookies on backend
      await api.post(API_ROUTES.USER_LOGOUT)
    } catch (err: any) {
      // Even if logout endpoint fails, continue with cleanup
      console.error('Logout endpoint error:', err)
    } finally {
      // Clear all localStorage items
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_id')

      // Clear auth state
      isAuthenticated.value = false
      user.value = null
      userRole.value = ''
      userIsReferrer.value = false

      // Clear user store
      const userStore = useUserStore()
      userStore.clearCurrentUser()
    }
  }

  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token')
      const refreshPayload = refreshTokenValue ? { jwt_refresh: refreshTokenValue } : {}

      const response = await api.post(API_ROUTES.USER_JWT_REFRESH, refreshPayload)

      // Store new tokens in localStorage as backup
      if (response.data.jwt_access) {
        localStorage.setItem('token', response.data.jwt_access)
      }
      if (response.data.jwt_refresh) {
        localStorage.setItem('refresh_token', response.data.jwt_refresh)
      }

      // Update reactive role from the new token
      updateRoleFromToken()

      return response.data
    } catch (err: any) {
      // If refresh fails, clear tokens
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_id')
      isAuthenticated.value = false
      userRole.value = ''
      userIsReferrer.value = false
      throw err
    }
  }

  const checkAuth = async (): Promise<boolean> => {
    try {
      const userStore = useUserStore()
      
      // Verify authentication via /user-get-current endpoint
      // This works with cookies (cross-domain) or Authorization header
      const response = await api.get(API_ROUTES.USER_GET_CURRENT)
      
      if (response.data && response.data.uuid) {
        isAuthenticated.value = true
        
        // If we have a token in response, store it in localStorage as backup
        // (Backend might return token in response for cross-subdomain scenarios)
        if (response.data.jwt_access) {
          localStorage.setItem('token', response.data.jwt_access)
        }
        
        // Try to update role from cookie or localStorage token
        updateRoleFromToken()
        
        // CROSS-SUBDOMAIN FIX: If no role found yet, try to get fresh JWT via refresh
        // This handles cases where:
        // - localStorage has no JWT (not shared across subdomains)
        // - user_role cookie is HttpOnly (JS can't read it)
        // - But refresh_token cookie IS available and can get us a fresh JWT
        if (!userRole.value) {
          try {
            // Try refreshing token - backend will use refresh cookie if available
            const refreshResponse = await api.post(API_ROUTES.USER_JWT_REFRESH, {})
            
            if (refreshResponse.data.jwt_access) {
              localStorage.setItem('token', refreshResponse.data.jwt_access)
              if (refreshResponse.data.jwt_refresh) {
                localStorage.setItem('refresh_token', refreshResponse.data.jwt_refresh)
              }
              // Now try to get role from the fresh token
              updateRoleFromToken()
            }
          } catch (refreshErr) {
            // Refresh failed, but user is still authenticated (checkAuth succeeded)
            // Role might still be empty - this is a problem that needs backend fix
            console.warn('Cross-subdomain auth: Could not refresh token to get role. user_role cookie may be HttpOnly.')
          }
        }
        
        // Set user data from response (we already have it, no need for another API call)
        // This is critical for cross-domain scenarios where userStore.currentUser is null
        // Router needs user data to determine role for redirects
        if (!userStore.currentUser || userStore.currentUser.uuid !== response.data.uuid) {
          // Directly set user data from the response we already received
          // This avoids making a redundant API call to fetchCurrentUser()
          userStore.setCurrentUser(response.data)
        }
        
        return true
      } else {
        // Invalid response, clear auth state
        isAuthenticated.value = false
        userRole.value = ''
        userIsReferrer.value = false
        localStorage.removeItem('token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user_id')
        userStore.clearCurrentUser()
        return false
      }
    } catch (err: any) {
      // Auth check failed, clear tokens and return false
      isAuthenticated.value = false
      userRole.value = ''
      userIsReferrer.value = false
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_id')
      const userStore = useUserStore()
      userStore.clearCurrentUser()
      return false
    }
  }

  const initAuth = async () => {
    // Always check auth status, even if no token in localStorage
    // This handles cross-subdomain scenarios where:
    // - Cookies are shared across subdomains
    // - localStorage is NOT shared across subdomains
    
    // First, sync role from any existing token in localStorage
    updateRoleFromToken()
    
    // Then verify auth status with backend
    // checkAuth() will also try to refresh token if role is not found
    const authenticated = await checkAuth()

    // Fetch access/status so Fellow isLimitedUser (has_access / tuition) is correct after login.
    if (authenticated) {
      try {
        const { useSubscriptionStore } = await import('./subscriptionAccess')
        const subscriptionStore = useSubscriptionStore()
        if (!subscriptionStore.isCacheFresh()) {
          await subscriptionStore.checkStatus({ preserveStateOnNetworkError: true })
        }
      } catch {
        // Non-fatal: Fellows stay non-limited until status loads (see useAuth isInitialized).
      }
    }
  }

  const setError = (message: string | null) => {
    error.value = message
  }

  const setTokens = async (tokens: { accessToken: string; refreshToken?: string }) => {
    const userStore = useUserStore()
    try {
      if (tokens.accessToken) {
        localStorage.setItem('token', tokens.accessToken)
        // Extract role from token
        try {
          const decoded = decodeJWT(tokens.accessToken)
          if (decoded?.role) {
            userRole.value = decoded.role.toUpperCase()
          }
          userIsReferrer.value = decoded?.is_referrer === true
        } catch (err) {
          console.warn('[Auth] Failed to decode token for role:', err)
        }
      }
      if (tokens.refreshToken) {
        localStorage.setItem('refresh_token', tokens.refreshToken)
      }
      isAuthenticated.value = true
      try {
        await userStore.fetchCurrentUser()
      } catch (err) {
        console.warn('[Auth] Failed to fetch user data after social auth:', err)
      }
      return true
    } catch (err) {
      console.error('[Auth] Failed to set tokens from social auth:', err)
      return false
    }
  }

  return {
    // State
    isAuthenticated,
    user,
    error,
    isLoading,
    // Reactive role state
    userRole,
    userIsReferrer,
    isGuide,
    isFellow,
    isCommon,
    isMentor,
    isReferrer,
    isAdmin,
    // Actions
    login,
    logout,
    refreshToken,
    checkAuth,
    initAuth,
    setError,
    updateRoleFromToken,
    getRoleFromCookie,
    setTokens
  }
}) 